import { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import clsx from 'clsx';
import { NAV } from '@/nav';
import { useAlarms } from '@/lib/query';
import { Droplet, Icon } from './Icons';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

export function Sidebar({ open, onClose }: SidebarProps) {
  const { data: alarms } = useAlarms();
  const activeCount = alarms?.active.length ?? 0;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <>
      {/* Scrim behind the mobile drawer only */}
      {open ? <div className="fixed inset-0 z-30 bg-black/30 lg:hidden" onClick={onClose} aria-hidden="true" /> : null}

      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-40 flex w-[260px] shrink-0 flex-col border-r border-hairline bg-panel transition-transform duration-200',
          'lg:static lg:translate-x-0',
          open ? 'translate-x-0 shadow-pop' : '-translate-x-full',
        )}
        aria-label="主导航"
      >
        <div className="p-4">
          <div className="flex items-start justify-between">
            <div className="rounded-card border border-hairline bg-gradient-to-b from-white to-brand-50/40 p-4 shadow-card">
              <div className="flex items-center gap-3">
                <Droplet size={44} />
                <div className="leading-tight">
                  <div className="text-[12px] text-slate-400">科研基地</div>
                  <div className="text-[14px] font-bold text-slate-800">湖北省农科院</div>
                  <div className="text-[13px] font-semibold text-brand-700">循环水吊水养殖科研基地</div>
                </div>
              </div>
              <div className="mt-2 text-[12px] text-slate-400">智能化监控与管理</div>
            </div>
            <button onClick={onClose} className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 hover:bg-slate-100 lg:hidden" aria-label="关闭导航">
              <Icon name="x" size={20} />
            </button>
          </div>
        </div>

        <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 pb-4">
          {NAV.map((item) => {
            const badge = item.badge === 'alarms' ? activeCount : item.badge;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                onClick={onClose}
                className={({ isActive }) => clsx('nav-item', isActive && 'nav-item-active')}
              >
                <Icon name={item.icon} size={19} />
                <span className="flex-1">{item.label}</span>
                {badge ? (
                  <span className="grid h-5 min-w-5 place-items-center rounded-full bg-alarm-fg px-1.5 text-[11px] font-semibold text-white" aria-label={`${item.label} ${badge} 条`}>
                    {badge}
                  </span>
                ) : null}
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-hairline p-4">
          <div className="flex items-center gap-2.5">
            <Droplet size={30} />
            <div className="leading-tight">
              <div className="text-[12px] text-slate-400">系统建设与运营</div>
              <div className="text-[12px] font-medium text-slate-500">湖北省扬子江智慧渔业科技有限公司</div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
